import { useEffect, useMemo, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import dayjs from 'dayjs';
import { twMerge } from 'tailwind-merge';
import Lucide from '@/chassis/ui/components/icons/Lucide';
import { REQUIRED_FIELD_MESSAGE, REQUIRED_VALIDATION_EVENT, type RequiredValidationEventDetail } from '@/chassis/data/services/form-required-validation';
import RequiredBadge from '../../visual-helper/RequiredBadge';

export interface RadixFormTimePickerProps {
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    className?: string;
    contentClassName?: string;
    label?: string;
    helperText?: string;
    error?: string;
    clearable?: boolean;
    format?: 'HH:mm' | 'HH:mm:ss';
    stepMinutes?: number;
    minTime?: string;
    maxTime?: string;
    id?: string;
    name?: string;
    requiredMessage?: string;
}

const INPUT_CLASSES = twMerge(
    'w-full h-10 rounded-md border border-slate-300/60 bg-white px-3 pr-10 text-sm text-slate-800 shadow-sm transition duration-200 ease-in-out',
    'placeholder:text-slate-400/90 focus:ring-4 focus:ring-primary focus:ring-opacity-20 focus:border-primary focus:border-opacity-40',
    'disabled:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400',
    'dark:border-darkmode-400 dark:bg-darkmode-700 dark:text-slate-100 dark:focus:ring-slate-700 dark:focus:ring-opacity-50 dark:placeholder:text-slate-500/80 dark:disabled:bg-darkmode-700/50',
);

const POPOVER_CLASSES = 'z-50 w-[240px] rounded-xl border border-slate-200 bg-white p-3 shadow-xl outline-none dark:border-darkmode-400 dark:bg-darkmode-800';

const pad = (n: number) => String(n).padStart(2, '0');

function buildOptions(stepMinutes: number, format: 'HH:mm' | 'HH:mm:ss') {
    const total = Math.max(1, Math.floor((24 * 60) / Math.max(stepMinutes, 1)));
    const options: string[] = [];
    for (let i = 0; i < total; i++) {
        const minutes = i * stepMinutes;
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        const base = `${pad(h)}:${pad(m)}`;
        options.push(format === 'HH:mm:ss' ? `${base}:00` : base);
    }
    // Ensure 23:59 or 23:59:59 is included for non-divisible steps
    if (!options.includes(format === 'HH:mm:ss' ? '23:59:59' : '23:59')) {
        options.push(format === 'HH:mm:ss' ? '23:59:59' : '23:59');
    }
    return options;
}

function maskTime(raw: string, format: 'HH:mm' | 'HH:mm:ss') {
    const digits = raw.replace(/\D/g, '');
    const maxLen = format === 'HH:mm:ss' ? 6 : 4;
    const clipped = digits.slice(0, maxLen);
    if (format === 'HH:mm:ss') {
        return clipped
            .replace(/(\d{2})(\d)/, '$1:$2')
            .replace(/(\d{2})(\d)/, '$1:$2')
            .replace(/(\d{2})(\d)/, '$1:$2');
    }
    return clipped.replace(/(\d{2})(\d)/, '$1:$2').replace(/(\d{2})(\d)/, '$1:$2');
}

function isValidTime(value: string, format: 'HH:mm' | 'HH:mm:ss') {
    const parsed = dayjs(`1970-01-01 ${value}`, `YYYY-MM-DD ${format}`, true);
    return parsed.isValid();
}

function parseTimeToMinutes(value: string | undefined, format: 'HH:mm' | 'HH:mm:ss'): number | null {
    if (!value) return null;
    const parsed = dayjs(`1970-01-01 ${value}`, `YYYY-MM-DD ${format}`, true);
    if (!parsed.isValid()) return null;
    return parsed.hour() * 60 + parsed.minute() + parsed.second() / 60;
}

function RadixFormTimePicker({
    value = '',
    onChange,
    placeholder = 'HH:MM',
    disabled,
    required,
    className,
    contentClassName,
    label,
    helperText,
    error,
    clearable = true,
    format = 'HH:mm',
    stepMinutes = 15,
    minTime,
    maxTime,
    id,
    name,
    requiredMessage = REQUIRED_FIELD_MESSAGE,
}: RadixFormTimePickerProps) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [showRequiredError, setShowRequiredError] = useState(false);

    const options = useMemo(() => {
        const all = buildOptions(stepMinutes, format);
        const minMinutes = parseTimeToMinutes(minTime, format);
        const maxMinutes = parseTimeToMinutes(maxTime, format);

        if (minMinutes === null && maxMinutes === null) return all;

        return all.filter((opt) => {
            const minutes = parseTimeToMinutes(opt, format);
            if (minutes === null) return false;
            if (minMinutes !== null && minutes < minMinutes) return false;
            if (maxMinutes !== null && minutes > maxMinutes) return false;
            return true;
        });
    }, [format, maxTime, minTime, stepMinutes]);
    const hasValue = Boolean(inputValue);

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    useEffect(() => {
        if (!required) return undefined;

        const listener = (event: Event) => {
            const detail = (event as CustomEvent<RequiredValidationEventDetail>).detail;
            const targetForm = detail?.form;
            const element = inputRef.current;

            if (!element) return;
            if (targetForm && element.form && targetForm !== element.form) return;
            if (!element.value || element.value.trim() === '') {
                setShowRequiredError(true);
            }
        };

        window.addEventListener(REQUIRED_VALIDATION_EVENT, listener as EventListener);
        return () => window.removeEventListener(REQUIRED_VALIDATION_EVENT, listener as EventListener);
    }, [required]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const masked = maskTime(event.target.value, format);
        setInputValue(masked);

        if (masked.length === (format === 'HH:mm:ss' ? 8 : 5) && isValidTime(masked, format)) {
            onChange?.(masked);
            if (required) {
                setShowRequiredError(false);
            }
        }
    };

    const handleSelect = (next: string) => {
        setInputValue(next);
        onChange?.(next);
        if (required) {
            setShowRequiredError(false);
        }
        setOpen(false);
    };

    const handleClear = () => {
        setInputValue('');
        onChange?.('');
        if (required) {
            setShowRequiredError(true);
        }
    };

    const handleBlur = () => {
        if (!required) return;
        setShowRequiredError(!inputValue || inputValue.trim() === '');
    };

    const inputError = error || (showRequiredError ? requiredMessage : undefined);
    const errorId = id ? `${id}-required` : `${(name || 'time')}-required`;

    return (
        <div className="space-y-1">
            {label ? (
                <div className="text-sm font-medium text-slate-700 dark:text-slate-200 inline-flex items-center gap-1">
                    <span>{label}</span>
                    {required ? <RequiredBadge /> : null}
                </div>
            ) : null}

            <Popover.Root open={open} onOpenChange={setOpen}>
                <Popover.Anchor asChild>
                    <div className="relative" ref={anchorRef}>
                        <input
                            type="text"
                            id={id}
                            name={name}
                            value={inputValue}
                            onChange={handleInputChange}
                            onFocus={() => setOpen(true)}
                            onClick={() => setOpen(true)}
                            placeholder={placeholder}
                            disabled={disabled}
                            onBlur={handleBlur}
                            required={required}
                            aria-invalid={inputError ? 'true' : undefined}
                            aria-describedby={inputError ? errorId : undefined}
                            ref={inputRef}
                            className={twMerge(
                                INPUT_CLASSES,
                                inputError && 'border-danger focus:border-danger focus:ring-danger/30',
                                className,
                            )}
                        />

                        <div className="pointer-events-none absolute inset-y-0 right-1.5 flex items-center gap-1 text-slate-500">
                            {clearable && hasValue && !disabled ? (
                                <button
                                    type="button"
                                    className="pointer-events-auto text-sm font-semibold text-slate-400 transition hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-200"
                                    onClick={handleClear}
                                    aria-label="Limpar horário"
                                >
                                    ×
                                </button>
                            ) : null}

                            <button
                                type="button"
                                className="pointer-events-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                                disabled={disabled}
                                aria-label="Abrir horários"
                                onClick={() => setOpen(true)}
                            >
                                <Lucide icon="Clock" className="h-4 w-4" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </Popover.Anchor>

                <Popover.Content
                    side="bottom"
                    align="start"
                    sideOffset={8}
                    onOpenAutoFocus={(event) => event.preventDefault()}
                    onCloseAutoFocus={(event) => event.preventDefault()}
                    onInteractOutside={(event) => {
                        const target = event.target as HTMLElement | null;
                        if (target && anchorRef.current?.contains(target)) {
                            event.preventDefault();
                        }
                    }}
                    className={twMerge(POPOVER_CLASSES, contentClassName)}
                >
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2 text-sm font-semibold text-slate-900 dark:border-darkmode-500/70 dark:text-slate-100">
                        <span>Escolha o horário</span>
                        <Popover.Close asChild>
                            <button
                                type="button"
                                className="flex h-8 items-center justify-center rounded-md border border-slate-200 px-2 text-xs font-semibold text-slate-700 transition hover:border-primary/50 hover:text-primary dark:border-darkmode-500/70 dark:text-slate-100"
                            >
                                Fechar
                            </button>
                        </Popover.Close>
                    </div>

                    <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50 p-2 dark:border-darkmode-500/60 dark:bg-darkmode-700/40">
                        <div className="grid grid-cols-3 gap-2 text-sm text-slate-800 dark:text-slate-100">
                            {options.map((opt) => {
                                const isSelected = opt === inputValue;
                                return (
                                    <button
                                        key={opt}
                                        type="button"
                                        className={twMerge(
                                            'rounded-md px-2 py-2 text-left transition',
                                            isSelected
                                                ? 'bg-primary text-white shadow-sm'
                                                : 'bg-white text-slate-800 hover:bg-primary/10 dark:bg-darkmode-600 dark:text-slate-100 dark:hover:bg-primary/20',
                                        )}
                                        onClick={() => handleSelect(opt)}
                                    >
                                        {opt}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </Popover.Content>
            </Popover.Root>

            {helperText && !inputError ? <p className="text-xs text-slate-500 dark:text-slate-400">{helperText}</p> : null}
            {inputError ? <p id={errorId} className="text-xs font-semibold text-rose-600 dark:text-rose-400">{inputError}</p> : null}
        </div>
    );
}

export default RadixFormTimePicker;
